import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  X,
  Upload,
  Check,
  Loader2,
  Image as ImageIcon,
  FileText,
} from "lucide-react";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://ckgo2-api.martin-94d.workers.dev";

interface MediaFile {
  key: string;
  url: string;
  size: number;
  uploaded: string;
  contentType: string;
}

interface ListResponse {
  files: MediaFile[];
  cursor?: string;
  hasMore: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  multiple?: boolean;
  onSelectMultiple?: (urls: string[]) => void;
}

function isImage(contentType: string, key?: string): boolean {
  if (contentType?.startsWith("image/")) return true;
  if (key?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) return true;
  return false;
}

export default function MediaPickerModal({
  isOpen,
  onClose,
  onSelect,
  multiple = false,
  onSelectMultiple,
}: Props) {
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch files
  const { data, isLoading } = useQuery({
    queryKey: ["media-files"],
    queryFn: () => api.get<ApiResponse<ListResponse>>("/api/admin/upload"),
    enabled: isOpen,
  });

  const files = data?.data?.files || [];
  const imageFiles = files.filter((f) => isImage(f.contentType, f.key));

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      formData.append("folder", "uploads");

      const response = await fetch(`${API_BASE}/api/admin/upload/multiple`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      return response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["media-files"] });
      // Auto-select newly uploaded files
      if (result.data?.uploaded && result.data.uploaded.length > 0) {
        if (multiple) {
          const newUrls = result.data.uploaded.map(
            (f: { url: string }) => f.url
          );
          setSelectedUrls((prev) => new Set([...prev, ...newUrls]));
        } else {
          // Single mode - select the first uploaded
          onSelect(result.data.uploaded[0].url);
          onClose();
        }
      }
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadMutation.mutate(e.target.files);
    }
  };

  // Handle drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        uploadMutation.mutate(e.dataTransfer.files);
      }
    },
    [uploadMutation]
  );

  // Toggle selection
  const toggleSelection = (url: string) => {
    if (multiple) {
      setSelectedUrls((prev) => {
        const next = new Set(prev);
        if (next.has(url)) {
          next.delete(url);
        } else {
          next.add(url);
        }
        return next;
      });
    } else {
      onSelect(url);
      onClose();
    }
  };

  // Confirm selection (multiple mode)
  const confirmSelection = () => {
    if (onSelectMultiple) {
      onSelectMultiple(Array.from(selectedUrls));
    }
    setSelectedUrls(new Set());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 mx-4 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {multiple ? "Vybrat obrázky" : "Vybrat obrázek"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-ghost text-sm"
            >
              <Upload className="h-4 w-4" />
              Nahrát
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className={`flex-1 overflow-y-auto p-6 ${
            isDragging ? "bg-primary-50 dark:bg-primary-900/10" : ""
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploadMutation.isPending && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-primary-50 p-4 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              Nahrávám...
            </div>
          )}

          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : imageFiles.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <ImageIcon className="mb-4 h-16 w-16 text-gray-300" />
              <p className="text-lg font-medium">Žádné obrázky</p>
              <p className="mt-1 text-sm">
                Přetáhněte obrázky sem nebo klikněte na "Nahrát"
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5">
              {imageFiles.map((file) => (
                <button
                  key={file.key}
                  onClick={() => toggleSelection(file.url)}
                  className={`group relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                    selectedUrls.has(file.url)
                      ? "border-primary-500 ring-2 ring-primary-500/20"
                      : "border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
                >
                  <img
                    src={file.url}
                    alt={file.key}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  {selectedUrls.has(file.url) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary-500/20">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-white">
                        <Check className="h-5 w-5" />
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Drag overlay */}
          {isDragging && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-primary-500" />
                <p className="mt-2 font-medium text-primary-600">
                  Pusťte pro nahrání
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer (multiple mode) */}
        {multiple && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <span className="text-sm text-gray-500">
              {selectedUrls.size} vybráno
            </span>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-ghost">
                Zrušit
              </button>
              <button
                onClick={confirmSelection}
                disabled={selectedUrls.size === 0}
                className="btn-primary"
              >
                Potvrdit výběr
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
